/**
 * Calendar-date helpers for booking flows (MPE-1169).
 *
 * A booking day is a CALENDAR DATE — the day the customer pointed at in the
 * picker. It is not an instant. Users cannot select a time, so nothing here may
 * ever convert a booking date through UTC or the browser timezone: a link
 * created in Spain for 31 August must still read 31 August when opened in Chile.
 *
 * The canonical representation is a `YYYY-MM-DD` string, carried verbatim from
 * the picker through URL, Redux and the search API. A `Date` is materialised
 * only where a UI calendar component demands one, and that conversion preserves
 * the same year, month and day.
 *
 * THREE SEPARATE CONCEPTS — deliberately not merged:
 *   1. CalendarDate        — which day (this module)
 *   2. activationWindow /
 *      daypart             — which hours within that day (`lib/dayparts.ts`)
 *   3. destination timezone — which market's clock defines "today"
 *                             (`lib/bookingTimezone.ts`)
 *
 * A CalendarDate never encodes a daypart or a timezone. When a future ticket
 * introduces real dayparts (MORNING, AFTERNOON, …), the date + daypart →
 * timestamp conversion must happen at the FINAL API BOUNDARY and resolve
 * against the destination screen/market timezone — never the buyer's browser
 * timezone. Today every booking is whole-day: `buildBookingWindow` sends the
 * calendar dates and the backend pins whole-day bounds per screen.
 */

/** A calendar date in `YYYY-MM-DD` form. */
export type CalendarDate = string

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

const pad = (value: number): string => String(value).padStart(2, "0")

/**
 * Whether `value` is a well-formed AND real calendar date.
 *
 * The round-trip check is what rejects impossible dates: `new Date(2026, 1, 31)`
 * silently rolls over to 3 March, so a value only passes if rebuilding it from
 * the constructed Date reproduces the original string. This matches the
 * backend, whose `GetAllMupisQuerySchema` uses Zod `.date()` and rejects
 * `2026-02-31`, `2026-13-01` and unpadded `2026-8-3` alike.
 */
export function isCalendarDate(value: unknown): boolean {
  if (typeof value !== "string") return false
  const match = CALENDAR_DATE_PATTERN.exec(value)
  if (!match) return false

  const [, year, month, day] = match
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  return date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day)
}

/**
 * Format a `Date` as a calendar date using its LOCAL parts.
 *
 * Never `toISOString()`: that converts to UTC, so a local midnight east of UTC
 * lands on the previous day (Madrid midnight 31 Aug → `2026-08-30T22:00:00Z`).
 * The picker hands back a Date whose local Y/M/D is the day the user clicked,
 * and those are exactly the parts we keep.
 */
export function toCalendarDate(date: Date | null | undefined): CalendarDate | undefined {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return undefined
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Parse a calendar date into a local `Date` with the identical year, month and
 * day, for the one place that needs it: the calendar component's `value`.
 *
 * Never `new Date("YYYY-MM-DD")`: the spec parses that as UTC midnight, which
 * renders as the previous day anywhere behind UTC.
 */
export function fromCalendarDate(value: string | null | undefined): Date | undefined {
  if (typeof value !== "string" || !isCalendarDate(value)) return undefined
  const match = CALENDAR_DATE_PATTERN.exec(value)
  if (!match) return undefined
  const [, year, month, day] = match
  return new Date(Number(year), Number(month) - 1, Number(day))
}

/**
 * Normalise an incoming URL/persisted value to a calendar date.
 *
 * A `YYYY-MM-DD` string is returned verbatim — no Date is constructed, so the
 * day cannot shift. This is the guaranteed path for every link this build
 * writes.
 *
 * LEGACY FALLBACK (best effort): links written before MPE-1169 stored a full
 * ISO instant with no record of the author's timezone, so the day they intended
 * is not recoverable. We take the browser-local calendar parts, which preserves
 * the behaviour those links have always had. It is deliberately NOT
 * reinterpreted in the destination timezone — that would look like a fix while
 * still guessing.
 */
export function coerceCalendarDate(value: string | null | undefined): CalendarDate | undefined {
  if (!value) return undefined
  if (isCalendarDate(value)) return value

  // A date-shaped value that failed validation is simply invalid — never hand
  // it to `new Date()`, which rolls impossible dates over instead of rejecting
  // them (`2026-02-31` → 3 March) and would silently invent a booking day.
  // Only a value carrying a time component can be a legacy instant.
  if (!value.includes("T")) return undefined

  const legacy = new Date(value)
  if (Number.isNaN(legacy.getTime())) return undefined
  return toCalendarDate(legacy)
}

/**
 * "Today" as a calendar date in the given market timezone.
 *
 * `en-CA` formats as `YYYY-MM-DD`, but the parts are read explicitly rather
 * than relying on that locale's output shape.
 */
export function todayInTimeZone(timeZone: string): CalendarDate {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date())

  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ""
  return `${get("year")}-${get("month")}-${get("day")}`
}

/** Shift a calendar date by whole days, staying in calendar space. */
export function addCalendarDays(value: string | null | undefined, days: number): CalendarDate | undefined {
  const date = fromCalendarDate(value)
  if (!date) return undefined
  date.setDate(date.getDate() + days)
  return toCalendarDate(date)
}

/**
 * Parse a booking date that is ALREADY unambiguous — a CalendarDate or a Date
 * from the picker — into a local Date.
 *
 * Deliberately NOT permissive: an arbitrary string returns `undefined` rather
 * than being handed to `new Date()`, which would guess at semantics and, for a
 * date-only string, silently shift the day in any zone behind UTC. Values with
 * a different provenance have their own narrow conversions:
 *   - legacy URL instants  → `coerceCalendarDate` (URL hydration only)
 *   - cart items → `cartItemBookingDates` (the backend's screen-clock days)
 *
 * A `Date` input is returned as a defensive copy, so callers may mutate the
 * result (several do, via `setDate`/`setHours`) without corrupting the source.
 */
export function toLocalDate(value: CalendarDate | Date | null | undefined): Date | undefined {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : new Date(value.getTime())
  }
  return fromCalendarDate(value)
}

/**
 * Whether calendar date `a` falls before `b`.
 *
 * Plain string comparison — `YYYY-MM-DD` is lexicographically ordered, so this
 * needs no Date and is immune to both timezone and DST.
 */
export function isCalendarDateBefore(a: CalendarDate, b: CalendarDate): boolean {
  return a < b
}
